import React, { useEffect } from "react";
import { AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight, Flag, PartyPopper, Sparkles } from "lucide-react";
import { primaryText, greyTextColor, borderPrimary, greenColorClass } from "../../../../styling/stylingHelper";

export type ReviewItemStatus = 'pending' | 'accepted' | 'flagged';

/** One reviewable entity in the sidebar + walkthrough (a dish, a subrecipe, …). */
export interface ReviewShellItem {
    id: string;
    primaryLabel: string;
    secondaryLabel?: string;
}

/**
 * Every string the shell renders, resolved by the host. The shell itself holds no
 * translation keys, so each flow keeps its own wording ("dishes" vs "subrecipes")
 * without the shell needing to know which flow it is.
 */
export interface ReviewShellLabels {
    title: string;
    back: string;
    listTitle: (count: number) => string;
    progress: (current: number, total: number) => string;
    progressLong: (done: number, total: number, percent: number) => string;
    acceptedPill: (count: number) => string;
    flaggedPill: (count: number) => string;
    prev: string;
    next: string;
    shortcutHint: string;
    flagForLater: string;
    looksGood: string;
    summaryTitle: string;
    summarySubtitle: (done: number, total: number) => string;
    summaryAccepted: string;
    summaryFlagged: string;
    summaryRemaining: string;
    summaryFlaggedListTitle: string;
    reviewAgain: string;
    finishReview: string;
}

interface ReviewShellProps {
    items: ReviewShellItem[];
    statuses: Record<string, ReviewItemStatus>;
    /** Equal to items.length renders the summary screen instead of a card. */
    currentIndex: number;
    labels: ReviewShellLabels;
    /** Marks an untouched item as still needing a look (red triangle in the list). */
    needsAttention: (itemId: string, index: number) => boolean;
    renderCard: (itemId: string, index: number) => React.ReactNode;
    onGoTo: (index: number) => void;
    onSetStatus: (itemId: string, status: ReviewItemStatus) => void;
    /** Back arrow — leaves the review without discarding it. */
    onExit: () => void;
    /** Finish button on the summary — the host clears the review and navigates. */
    onFinish: () => void;
    /** Suppresses keyboard shortcuts while an editor slide-over is open above the shell. */
    shortcutsDisabled?: boolean;
    /** Optional extra block above the summary's finish button (per-flow extras). */
    summaryExtra?: React.ReactNode;
}

/**
 * The chrome every automation review walkthrough shares: progress header, a
 * jump-anywhere sidebar, one card at a time with prev/next + accept/flag, a
 * summary screen, and keyboard-driven navigation. The host owns the data, the
 * card and the persistence; this owns the frame.
 */
const ReviewShell: React.FC<ReviewShellProps> = ({
    items,
    statuses,
    currentIndex,
    labels,
    needsAttention,
    renderCard,
    onGoTo,
    onSetStatus,
    onExit,
    onFinish,
    shortcutsDisabled,
    summaryExtra,
}) => {
    const totalItems = items.length;
    const isSummary = currentIndex >= totalItems;
    const currentItem = isSummary ? null : items[currentIndex];

    const flaggedItems = items.filter(item => statuses[item.id] === 'flagged');
    const acceptedCount = items.filter(item => statuses[item.id] === 'accepted').length;
    const reviewedCount = acceptedCount + flaggedItems.length;
    const progressPercent = totalItems > 0 ? Math.round((reviewedCount / totalItems) * 100) : 0;

    const markAndAdvance = (status: 'accepted' | 'flagged') => {
        if (!currentItem) return;
        onSetStatus(currentItem.id, status);
        onGoTo(currentIndex + 1);
    };

    // Keyboard-driven review: arrows to move, A/Enter to accept, F to flag. Skipped
    // while the user is typing in a field or an editor slide-over is open.
    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (shortcutsDisabled) return;
            const target = event.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
            if (event.metaKey || event.ctrlKey || event.altKey) return;

            if (event.key === 'ArrowLeft') {
                onGoTo(currentIndex - 1);
            } else if (event.key === 'ArrowRight') {
                onGoTo(currentIndex + 1);
            } else if (currentItem && (event.key === 'a' || event.key === 'A' || event.key === 'Enter')) {
                markAndAdvance('accepted');
            } else if (currentItem && (event.key === 'f' || event.key === 'F')) {
                markAndAdvance('flagged');
            } else {
                return;
            }
            event.preventDefault();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
        // No dependency array on purpose: the handler closes over the current item
        // index and must be rebound each render to stay in sync with it.
    });

    return (
        <div className="min-h-screen bg-backgroundSecondary dark:bg-backgroundSecondary-dark flex flex-col">
            {/* Header: back + progress + score pills */}
            <div className={`sticky top-0 z-20 bg-background dark:bg-background-dark border-b ${borderPrimary} px-4 sm:px-6 py-3`}>
                <div className="flex items-center gap-4 flex-wrap">
                    <button
                        type="button"
                        onClick={onExit}
                        title={labels.back}
                        className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark ${greyTextColor}`}
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-accent dark:text-accent-dark" />
                        <h1 className={`text-[18px] font-bold ${primaryText}`}>{labels.title}</h1>
                    </div>

                    <div className="flex-1 min-w-[180px] flex items-center gap-3">
                        <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                            <div
                                className="h-full bg-darkGreenColor dark:bg-darkGreenColor-dark transition-all duration-300"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <span className={`text-[13px] font-semibold ${greyTextColor} whitespace-nowrap`}>
                            {labels.progressLong(reviewedCount, totalItems, progressPercent)}
                        </span>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                            {labels.acceptedPill(acceptedCount)}
                        </span>
                        <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            {labels.flaggedPill(flaggedItems.length)}
                        </span>
                    </div>
                </div>
            </div>

            <div className="flex-1 flex gap-5 p-4 sm:p-6 max-w-[1600px] w-full mx-auto">
                {/* Sidebar: every item, jump anywhere */}
                <aside className={`hidden lg:flex flex-col w-72 flex-shrink-0 rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark overflow-hidden self-start max-h-[calc(100vh-140px)]`}>
                    <p className={`text-[13px] font-semibold ${primaryText} px-4 py-3 border-b ${borderPrimary}`}>
                        {labels.listTitle(totalItems)}
                    </p>
                    <div className="overflow-y-auto py-1">
                        {items.map((item, index) => {
                            const status = statuses[item.id];
                            const isCurrent = index === currentIndex;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => onGoTo(index)}
                                    className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors ${
                                        isCurrent
                                            ? 'bg-[#F1FAF7] dark:bg-green-900/20'
                                            : 'hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark'
                                    }`}
                                >
                                    <span className={`text-[11px] w-6 flex-shrink-0 ${greyTextColor}`}>{index + 1}</span>
                                    <span className="min-w-0 flex-1">
                                        <span className={`block text-[13px] truncate ${isCurrent ? 'font-semibold' : 'font-medium'} ${primaryText}`}>
                                            {item.primaryLabel}
                                        </span>
                                        {item.secondaryLabel && (
                                            <span className={`block text-[11px] truncate ${greyTextColor}`}>{item.secondaryLabel}</span>
                                        )}
                                    </span>
                                    {status === 'accepted' && <Check className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />}
                                    {status === 'flagged' && <Flag className="h-4 w-4 flex-shrink-0 text-amber-500 dark:text-darkOrangeColor-dark" />}
                                    {(!status || status === 'pending') && needsAttention(item.id, index) && (
                                        <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-textRed-dark" />
                                    )}
                                </button>
                            );
                        })}
                        <button
                            type="button"
                            onClick={() => onGoTo(totalItems)}
                            className={`w-full px-4 py-2.5 text-left text-[13px] font-semibold ${
                                isSummary ? 'bg-[#F1FAF7] dark:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark'
                            } ${primaryText}`}
                        >
                            {labels.summaryTitle}
                        </button>
                    </div>
                </aside>

                {/* Main column */}
                <main className="flex-1 min-w-0 flex flex-col gap-4">
                    {currentItem ? (
                        <>
                            <div className="flex items-center justify-between gap-3">
                                <span className={`text-[13px] ${greyTextColor}`}>
                                    {labels.progress(currentIndex + 1, totalItems)}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => onGoTo(currentIndex - 1)}
                                        disabled={currentIndex === 0}
                                        title={labels.prev}
                                        className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark disabled:opacity-30 ${greyTextColor}`}
                                    >
                                        <ChevronLeft className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onGoTo(currentIndex + 1)}
                                        title={labels.next}
                                        className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark ${greyTextColor}`}
                                    >
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>

                            {renderCard(currentItem.id, currentIndex)}

                            {/* Item-level actions */}
                            {/* bottom-[68px] below lg lifts the bar above the fixed BottomBar */}
                            <div className={`sticky bottom-[68px] lg:bottom-0 z-30 flex items-center gap-3 rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark px-4 py-3`}>
                                <span className={`hidden sm:block text-[12px] ${greyTextColor}`}>
                                    {labels.shortcutHint}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => markAndAdvance('flagged')}
                                    className={`ml-auto h-11 px-5 flex items-center justify-center gap-2 text-[14px] font-semibold rounded-lg border ${borderPrimary} hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600 dark:text-amber-400`}
                                >
                                    <Flag className="h-4 w-4" />
                                    {labels.flagForLater}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => markAndAdvance('accepted')}
                                    className={`h-11 px-6 justify-center gap-2 text-[15px] font-semibold ${greenColorClass}`}
                                >
                                    <Check className="h-4 w-4" />
                                    {labels.looksGood}
                                </button>
                            </div>
                        </>
                    ) : (
                        /* Summary screen */
                        <div className={`rounded-xl border ${borderPrimary} bg-background dark:bg-background-dark p-6 space-y-5`}>
                            <div className="flex items-center gap-3">
                                <PartyPopper className="h-7 w-7 text-darkGreenColor dark:text-darkGreenColor-dark" />
                                <div>
                                    <h2 className={`text-[20px] font-bold ${primaryText}`}>{labels.summaryTitle}</h2>
                                    <p className={`text-[13px] ${greyTextColor}`}>
                                        {labels.summarySubtitle(reviewedCount, totalItems)}
                                    </p>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <div className={`flex-1 rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-4 py-3`}>
                                    <p className="text-[24px] font-bold text-green-600 dark:text-green-400">{acceptedCount}</p>
                                    <p className={`text-[13px] ${greyTextColor}`}>{labels.summaryAccepted}</p>
                                </div>
                                <div className={`flex-1 rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-4 py-3`}>
                                    <p className="text-[24px] font-bold text-amber-600 dark:text-amber-400">{flaggedItems.length}</p>
                                    <p className={`text-[13px] ${greyTextColor}`}>{labels.summaryFlagged}</p>
                                </div>
                                <div className={`flex-1 rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-4 py-3`}>
                                    <p className={`text-[24px] font-bold ${primaryText}`}>{totalItems - reviewedCount}</p>
                                    <p className={`text-[13px] ${greyTextColor}`}>{labels.summaryRemaining}</p>
                                </div>
                            </div>

                            {flaggedItems.length > 0 && (
                                <div>
                                    <p className={`text-[14px] font-semibold ${primaryText} mb-2`}>
                                        {labels.summaryFlaggedListTitle}
                                    </p>
                                    <div className="space-y-2">
                                        {flaggedItems.map(item => (
                                            <div
                                                key={item.id}
                                                className={`flex items-center justify-between rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 py-2`}
                                            >
                                                <div>
                                                    <p className={`text-[14px] font-semibold ${primaryText}`}>{item.primaryLabel}</p>
                                                    {item.secondaryLabel && (
                                                        <p className={`text-[12px] ${greyTextColor}`}>{item.secondaryLabel}</p>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => onGoTo(items.indexOf(item))}
                                                    className={`text-[13px] font-semibold px-3 py-1.5 rounded-lg border ${borderPrimary} hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark ${primaryText}`}
                                                >
                                                    {labels.reviewAgain}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {summaryExtra}

                            <button
                                type="button"
                                onClick={onFinish}
                                className={`w-full h-11 justify-center text-[15px] font-semibold ${greenColorClass}`}
                            >
                                {labels.finishReview}
                            </button>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

/** Shown when a review page is opened with no review in progress for this restaurant. */
export const ReviewEmptyState: React.FC<{ message: string; actionLabel: string; onAction: () => void }> = ({
    message,
    actionLabel,
    onAction,
}) => (
    <div className="min-h-screen bg-backgroundSecondary dark:bg-backgroundSecondary-dark flex flex-col items-center justify-center gap-4 p-6">
        <p className={`text-[16px] ${greyTextColor}`}>{message}</p>
        <button
            type="button"
            onClick={onAction}
            className={`h-11 px-5 justify-center text-[15px] font-semibold ${greenColorClass}`}
        >
            {actionLabel}
        </button>
    </div>
);

export default ReviewShell;
